import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, createClient } from '@/services/supabase';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface InvitationData {
  token: string;
  email: string;
  role: string;
  businessId?: string;
  charityId?: string;
  name?: string;
  invitedBy?: string;
  organizationType: 'business' | 'charity';
}

export default function AcceptInvitation() {
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const parseInvitation = async () => {
      try {
        // Get the token from the URL query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        
        if (!token) {
          setError('Invalid invitation link. No token provided.');
          setIsLoading(false);
          return;
        }
        
        // Verify the token directly with Supabase
        const client = createClient();
        const { data, error } = await (await client)
          .from('invitations')
          .select('*')
          .eq('token', token)
          .single();
        
        if (data && !error) {
          setInvitationData({
            token,
            email: data.email,
            role: data.role,
            businessId: data.metadata?.businessId,
            charityId: data.metadata?.charityId,
            name: data.metadata?.name,
            invitedBy: data.metadata?.invitedBy,
            organizationType: data.metadata?.businessId ? 'business' : 'charity'
          });
          
          // Prefill name if available in invitation
          if (data.metadata?.name) {
            setName(data.metadata.name);
          }
        } else {
          setError('Invalid or expired invitation.');
        }
      } catch (error) {
        console.error('Error verifying invitation:', error);
        setError('Failed to verify invitation. It may have expired or been revoked.');
      } finally {
        setIsLoading(false);
      }
    };
    
    parseInvitation();
  }, [location]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invitationData) {
      setError('Invalid invitation data.');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Check if user already exists with this email
      const { data: existingUser } = await supabase.auth.signInWithPassword({
        email: invitationData.email,
        password,
      });
      
      if (existingUser) {
        // User exists, just accept the invitation directly with Supabase
        const client = createClient();
        
        // Update the invitation status in the database
        await (await client)
          .from('invitations')
          .update({ 
            status: 'accepted',
            accepted_at: new Date().toISOString(),
            user_id: existingUser.user?.id 
          })
          .eq('token', invitationData.token);
          
        // Add the user to the appropriate organization staff table
        if (invitationData.businessId) {
          await (await client)
            .from('business_staff')
            .insert({
              user_id: existingUser.user?.id,
              business_id: invitationData.businessId,
              role: invitationData.role,
              status: 'active'
            });
        } else if (invitationData.charityId) {
          await (await client)
            .from('charity_staff')
            .insert({
              user_id: existingUser.user?.id,
              charity_id: invitationData.charityId,
              role: invitationData.role,
              status: 'active'
            });
        }
      } else {
        // Create new user and accept invitation
        const { data: newUser, error: signUpError } = await supabase.auth.signUp({
          email: invitationData.email,
          password,
          options: {
            data: {
              name,
              role: invitationData.role,
            },
          },
        });
        
        if (signUpError) {
          throw signUpError;
        }
        
        // Accept the invitation directly with Supabase
        const client = createClient();
        
        // Update the invitation status in the database
        await (await client)
          .from('invitations')
          .update({ 
            status: 'accepted',
            accepted_at: new Date().toISOString(),
            user_id: newUser?.user?.id 
          })
          .eq('token', invitationData.token);
          
        // Add the user to the appropriate organization staff table
        if (invitationData.businessId) {
          await (await client)
            .from('business_staff')
            .insert({
              user_id: newUser?.user?.id,
              business_id: invitationData.businessId,
              role: invitationData.role,
              status: 'active',
              first_name: name.split(' ')[0],
              last_name: name.split(' ').slice(1).join(' ') || ''
            });
        } else if (invitationData.charityId) {
          await (await client)
            .from('charity_staff')
            .insert({
              user_id: newUser?.user?.id,
              charity_id: invitationData.charityId,
              role: invitationData.role,
              status: 'active',
              first_name: name.split(' ')[0],
              last_name: name.split(' ').slice(1).join(' ') || ''
            });
        }
      }
      
      setSuccess(true);
      
      // Redirect after a delay
      setTimeout(() => {
        if (invitationData.organizationType === 'business') {
          navigate('/business/dashboard');
        } else {
          navigate('/charities/dashboard');
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy-blue"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invitation Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-navy-blue text-white py-2 px-4 rounded-md hover:bg-sky-blue transition-colors duration-200"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invitation Accepted!</h1>
          <p className="text-gray-600 mb-6">
            You have successfully accepted the invitation. You will be redirected to the dashboard shortly.
          </p>
          <div className="mt-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-navy-blue mr-2"></div>
            <span className="text-gray-500">Redirecting...</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">Accept Invitation</h1>
        
        {invitationData && (
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-semibold">From:</span> {invitationData.invitedBy || 'Organization Admin'}
            </p>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-semibold">Organization Type:</span> {invitationData.organizationType === 'business' ? 'Business' : 'Charity'}
            </p>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-semibold">Role:</span> {invitationData.role.charAt(0).toUpperCase() + invitationData.role.slice(1)}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Email:</span> {invitationData.email}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-navy-blue focus:ring-navy-blue"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={invitationData?.email || ''}
              disabled
              className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
            />
            <p className="mt-1 text-xs text-gray-500">You cannot change the email address.</p>
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-navy-blue focus:ring-navy-blue"
            />
          </div>
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-navy-blue focus:ring-navy-blue"
            />
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-navy-blue text-white py-2 px-4 rounded-md hover:bg-sky-blue transition-colors duration-200 flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                'Accept & Create Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
